import React, { useId, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase } from 'lucide-react';
import DisabledReason, { deniedReason } from '../DisabledReason';
import DealModal from '../deals/DealModal';
import DealSearchSelect from '../deals/DealSearchSelect';
import { errorText } from '../../lib/apiErrors';
import { dealStageMeta } from '../../lib/dealStages';
import { personLabel } from '../../lib/teammateLabels';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const GROUP_LIMIT = 5;
// Why a control is disabled, in the wording the Contact Detail header uses for its own permission gates.
const CREATE_REASON = 'You do not have permission to create deals.';
const LINK_REASON = 'You do not have permission to update deals.';
const ACTION = 'px-2.5 py-1 text-xs font-medium rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent';

function DealRow({ deal, definition, onOpen }) {
    const amount = deal.value || deal.amount;
    return (
        <button type="button" onClick={() => onOpen(deal.deal_id)} className="w-full text-left p-2.5 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-100/30 transition-colors" data-testid={`deal-${deal.deal_id}`}>
            <p className="text-sm font-medium truncate text-stone-800">{deal.title || deal.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-stone-400">{dealStageMeta(definition, deal.stage).label}</span>
                {amount ? <span className="text-xs font-medium text-emerald-600">${Number(amount).toLocaleString()}</span> : null}
            </div>
            <p className="mt-0.5 text-[11px] truncate text-stone-400" data-testid="deal-owner">Deal Owner: {personLabel(deal.owner_id, deal.deal_owner_name) || 'Unassigned'}</p>
        </button>
    );
}

// One group of the card: the first GROUP_LIMIT rows, and "Show all" expands the rest in place.
function DealGroup({ id, title, deals, expanded, onToggle, definition, onOpen }) {
    if (deals.length === 0) return null;
    const shown = expanded ? deals : deals.slice(0, GROUP_LIMIT);
    return (
        <div data-testid={`deals-group-${id}`}>
            <p className="mb-1.5 text-[11px] font-semibold text-stone-500">{title}</p>
            <div className="space-y-2">
                {shown.map(deal => <DealRow key={deal.deal_id} deal={deal} definition={definition} onOpen={onOpen} />)}
            </div>
            {deals.length > GROUP_LIMIT && (
                <button type="button" onClick={onToggle} aria-expanded={expanded} className="mt-1.5 text-[11px] font-medium text-stone-600 hover:underline" data-testid={`deals-show-all-${id}`}>
                    {expanded ? 'Show fewer' : `Show all (${deals.length})`}
                </button>
            )}
        </div>
    );
}

/**
 * Contact Detail's Deals card (#906). `linkedDeals` name this contact (a missing `contact_link` counts as
 * linked); `companyDeals` match only through the contact's company and sit in their own group, so a company
 * deal is never presented as the contact's own. The header counts the linked deals only. "New deal" needs
 * `canCreate` (the server's `deals:create`) and "Add to existing deal" needs `canLink` (`deals:update`); a
 * viewer without one sees that button disabled, with the reason as its title and, for assistive tech, as its
 * description (a disabled control's title is a mouse tooltip only). While `permissionsUnknown` the buttons are
 * disabled and give no reason: nobody has said no yet. A deleted contact gets neither button. Both end in
 * `onChanged()`, which re-reads the deals.
 */
export default function ContactDealsCard({
    contact, account, linkedDeals, companyDeals, failed, definition, onStageRejected,
    canCreate, canLink, permissionsUnknown = false, getAuthHeader, onChanged, onOpenDeal,
}) {
    const [showAllLinked, setShowAllLinked] = useState(false);
    const [showAllCompany, setShowAllCompany] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [picking, setPicking] = useState(false);
    const [linking, setLinking] = useState(false);
    const excludeIds = useMemo(() => linkedDeals.map(deal => deal.deal_id), [linkedDeals]);
    const noDeals = linkedDeals.length === 0 && companyDeals.length === 0;
    const writable = !contact.is_deleted;
    const createReason = deniedReason(canCreate, permissionsUnknown, CREATE_REASON);
    const linkReason = deniedReason(canLink, permissionsUnknown, LINK_REASON);
    const reasonId = useId();

    const linkDeal = async (deal) => {
        if (linking) return;
        setLinking(true);
        try {
            await axios.post(`${API}/deals/${deal.deal_id}/stakeholders`, { contact_id: contact.contact_id }, { headers: getAuthHeader() });
            toast.success(`Added to ${deal.title || 'the deal'}`);
            setPicking(false);
            onChanged();
        } catch (err) {
            toast.error(errorText(err, 'Could not add this contact to the deal'));
        } finally { setLinking(false); }
    };

    return (
        <div id="contact-deals-card" className="p-4 border-b border-stone-100 scroll-mt-16" data-testid="contact-deals-card">
            <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Deals ({failed ? '—' : linkedDeals.length})</h4>
            {failed ? (
                <div className="py-3 text-center" data-testid="deals-error">
                    <p className="text-xs text-stone-500">Deals could not be loaded.</p>
                    <button type="button" onClick={() => onChanged()} className="mt-1 text-xs font-medium text-stone-700 underline" data-testid="deals-retry">Retry</button>
                </div>
            ) : noDeals ? (
                <div className="py-4 text-center">
                    <Briefcase className="mx-auto mb-1 w-8 h-8 text-stone-200" />
                    <p className="text-xs text-stone-400">No associated deals</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <DealGroup id="linked" title="Linked" deals={linkedDeals} expanded={showAllLinked}
                        onToggle={() => setShowAllLinked(v => !v)} definition={definition} onOpen={onOpenDeal} />
                    <DealGroup id="company" title={`Other deals at ${account?.name || 'this company'}`} deals={companyDeals} expanded={showAllCompany}
                        onToggle={() => setShowAllCompany(v => !v)} definition={definition} onOpen={onOpenDeal} />
                </div>
            )}
            {writable && (
                <div className="flex flex-wrap gap-2 mt-3">
                    <button type="button" onClick={() => setModalOpen(true)} disabled={!canCreate} title={createReason}
                        aria-describedby={createReason ? `${reasonId}-create` : undefined}
                        className={ACTION} data-testid="deal-new-btn">New deal</button>
                    <DisabledReason id={`${reasonId}-create`} reason={createReason} />
                    <button type="button" onClick={() => setPicking(v => !v)} aria-expanded={picking} disabled={!canLink} title={linkReason}
                        aria-describedby={linkReason ? `${reasonId}-link` : undefined}
                        className={ACTION} data-testid="deal-add-existing-btn">Add to existing deal</button>
                    <DisabledReason id={`${reasonId}-link`} reason={linkReason} />
                </div>
            )}
            {writable && canLink && picking && (
                <div className="mt-2">
                    <DealSearchSelect testId="contact-deal-search" excludeIds={excludeIds} onPick={linkDeal} placeholder="Search deals to link…" />
                </div>
            )}
            {writable && canCreate && modalOpen && (
                <DealModal
                    open
                    onClose={() => setModalOpen(false)}
                    deal={null}
                    initial={{
                        account_id: contact.account_id || '',
                        account_name: account?.name || '',
                        contacts: [{ contact_id: contact.contact_id, first_name: contact.first_name || '', last_name: contact.last_name || '', email: contact.email || '' }],
                    }}
                    definition={definition}
                    onSave={() => onChanged()}
                    onStageRejected={onStageRejected}
                />
            )}
        </div>
    );
}
